import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Address,
  CreateCompanyShippingAddressParams,
  CompanyShippingAddress,
  IsoCountryCode,
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
} from "@forgepoint/shared";
import { useCompanyShippingAddressesStore } from "@/modules/companyShippingAddresses/companyShippingAddressesStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader, Plus, Trash2 } from "lucide-react";
import { UserSelector } from "@/components/UserSelector";
import { useUserDisplayNames } from "@/store/teamsStore";
import { useAllowShipToCreation } from "@/store/quoteSettingsStore";

interface ShippingAddressesSectionProps {
  companyId: string;
}

const createEmptyAddress = (): Address => ({
  name: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  zipcode: null,
  country: null,
});

interface ShippingAddressFormData {
  label: string;
  address: Address;
  salesRepUserId: string | null;
}

const createEmptyFormData = (): ShippingAddressFormData => ({
  label: "",
  address: createEmptyAddress(),
  salesRepUserId: null,
});

/**
 * Section for managing shipping addresses within the company edit sheet.
 * Lists, creates, edits, and deletes shipping addresses for a company.
 * @param companyId - The company whose shipping addresses to manage
 */
export function ShippingAddressesSection({
  companyId,
}: ShippingAddressesSectionProps) {
  const { shippingAddresses, fetchByCompany, create, update, remove } =
    useCompanyShippingAddressesStore();
  const userDisplayNames = useUserDisplayNames();
  const allowShipToCreation = useAllowShipToCreation();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ShippingAddressFormData>(
    createEmptyFormData(),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchByCompany(companyId);
  }, [companyId, fetchByCompany]);

  const handleAdd = useCallback(() => {
    setEditingId(null);
    setFormData(createEmptyFormData());
    setIsSheetOpen(true);
  }, []);

  const companyAddresses = useMemo(() => {
    const result: CompanyShippingAddress[] = [];
    shippingAddresses.forEach((addr) => {
      if (addr.companyId === companyId) {
        result.push(addr);
      }
    });
    return result;
  }, [shippingAddresses, companyId]);

  const countryOptions = useMemo(
    () =>
      ISO_3166_1_ALPHA_2_CODES.map(
        (code): { code: IsoCountryCode; label: string } => ({
          code,
          label: code,
        }),
      ),
    [],
  );

  const updateAddressField = <K extends keyof Address>(
    key: K,
    value: Address[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));
  };

  const handleCountrySelect = (value: string) => {
    if (value === "") {
      updateAddressField("country", null);
      return;
    }
    if (!isISO31661Alpha2Code(value)) {
      return;
    }
    updateAddressField("country", value);
  };

  const handleEdit = useCallback((addr: CompanyShippingAddress) => {
    setEditingId(addr.id);
    setFormData({
      label: addr.label,
      address: { ...addr.address },
      salesRepUserId: addr.salesRepUserId,
    });
    setIsSheetOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      const success = await remove(companyId, id);
      if (success) {
        toast.success("Shipping address deleted");
      } else {
        toast.error("Failed to delete shipping address");
      }
    },
    [companyId, remove],
  );

  const handleSave = useCallback(async () => {
    if (!formData.label.trim()) {
      toast.error("Label is required");
      return;
    }

    setIsSaving(true);
    try {
      if (editingId) {
        const result = await update(companyId, editingId, {
          label: formData.label,
          address: formData.address,
          salesRepUserId: formData.salesRepUserId,
        });
        if (result) {
          toast.success("Shipping address updated");
          setIsSheetOpen(false);
        } else {
          toast.error("Failed to update shipping address");
        }
      } else {
        const payload: CreateCompanyShippingAddressParams = {
          label: formData.label,
          address: formData.address,
          salesRepUserId: formData.salesRepUserId,
        };
        const result = await create(companyId, payload);
        if (result) {
          toast.success("Shipping address created");
          setIsSheetOpen(false);
        } else {
          toast.error("Failed to create shipping address");
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [editingId, companyId, formData, create, update]);

  const formatAddressPreview = (address: Address): string => {
    const parts = [
      address.address_line_1,
      address.city,
      address.state,
      address.zipcode,
    ].filter(Boolean);
    return parts.join(", ") || "No address";
  };

  const addressColumns: DataTableColumn<CompanyShippingAddress>[] = useMemo(
    () => [
      {
        key: "label",
        header: "Label",
        type: "string",
      },
      {
        key: "address",
        header: "Address",
        type: "string",
        format: (_value: unknown, addr: CompanyShippingAddress) =>
          formatAddressPreview(addr.address),
      },
      {
        key: "salesRepUserId",
        header: "Sales Rep",
        type: "string",
        width: 160,
        format: (_value: unknown, addr: CompanyShippingAddress) =>
          addr.salesRepUserId
            ? (userDisplayNames.get(addr.salesRepUserId) ?? "-")
            : "-",
      },
      {
        key: "_add",
        header: allowShipToCreation ? (
          <button
            type="button"
            onClick={handleAdd}
            className="group inline-flex items-center justify-center rounded-md p-1 text-gray-400 transition-all duration-150 ease-out hover:text-primary hover:bg-primary/10 active:scale-90"
          >
            <Plus className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:rotate-90" />
          </button>
        ) : null,
        width: 36,
        align: "center",
        format: () => null,
      },
    ],
    [userDisplayNames, handleAdd, allowShipToCreation],
  );

  return (
    <div>
      <DataTable
        columns={addressColumns}
        data={companyAddresses}
        hideSearch
        onClickRow={(addr) => handleEdit(addr)}
      />

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:w-[500px] sm:max-w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingId ? "Edit Shipping Address" : "Add Shipping Address"}
            </SheetTitle>
          </SheetHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="shipping-label">Label *</Label>
              <Input
                id="shipping-label"
                value={formData.label}
                onChange={(e) =>
                  setFormData({ ...formData, label: e.target.value })
                }
                placeholder='e.g. "Main Warehouse", "West Coast DC"'
              />
            </div>

            <div className="grid gap-2">
              <Label>Address</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Name / Attention"
                  value={formData.address.name || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("name", value || null);
                  }}
                />
                <Input
                  placeholder="Address Line 1"
                  value={formData.address.address_line_1 || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("address_line_1", value || null);
                  }}
                />
                <Input
                  placeholder="Address Line 2"
                  value={formData.address.address_line_2 || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("address_line_2", value || null);
                  }}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="City"
                    value={formData.address.city || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateAddressField("city", value || null);
                    }}
                  />
                  <Input
                    placeholder="State"
                    value={formData.address.state || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateAddressField("state", value || null);
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Zip Code"
                    value={formData.address.zipcode || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateAddressField("zipcode", value || null);
                    }}
                  />
                  <Select
                    value={formData.address.country || ""}
                    onValueChange={handleCountrySelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map(({ code, label }) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Sales Rep</Label>
              <UserSelector
                currentlySelectedId={formData.salesRepUserId}
                onSelectId={(id) =>
                  setFormData({ ...formData, salesRepUserId: id })
                }
                placeholder="Select sales rep (optional)"
                size="default"
              />
            </div>
          </div>

          <SheetFooter className="flex-col gap-2 sm:flex-row">
            {editingId && (
              <Button
                variant="destructive"
                disabled={isSaving}
                className="w-full sm:w-auto sm:mr-auto"
                onClick={async () => {
                  await handleDelete(editingId);
                  setIsSheetOpen(false);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
            )}
            <SheetClose asChild>
              <Button
                variant="outline"
                disabled={isSaving}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </SheetClose>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
              {editingId ? "Save Changes" : "Add Address"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
